import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getData } from "country-list";
import { useRankings } from "../apiCalls/userApiCalls";
import { useStats } from "../apiCalls/statsApiCalls";
import { useClubs } from "../apiCalls/clubApiCalls";
import {
  Container,
  Card,
  Button,
  Input,
  Select,
  Label,
  SearchIcon,
  CountryLabel,
  RankBadge,
  RankMovement,
  VerifiedTick,
  StatStrip,
} from "../components/ui";
import Pagination from "../components/ui/Pagination";
import {
  UsersIcon,
  GlobeIcon,
  TrophyIcon,
  RefreshIcon,
  TrendIcon,
  ChevronRightIcon,
  ArrowRightIcon,
} from "../components/ui/icons";
import normalDate, { formatNumber } from "../utils";
import Loader from "../components/Loader";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";

const countries = getData();
const PAGE_SIZE = 10;

// The gender segmented control. "Mixed / Open" has no data behind it yet —
// Player.gender is Male/Female only — so it is offered but honest about being
// empty rather than silently showing the men's list.
const SEGMENTS = [
  { value: "Male", label: "Men" },
  { value: "Female", label: "Women" },
  { value: "Mixed", label: "Mixed / Open" },
];

const SearchRankings = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [gender, setGender] = useState("Male");
  const [nationality, setNationality] = useState("");
  const [club, setClub] = useState("");

  const isMixed = gender === "Mixed";

  const { data, isFetching, isError } = useRankings(
    page,
    search,
    isMixed ? "" : gender,
    nationality,
    PAGE_SIZE
  );
  const { data: statsRes } = useStats();
  const { data: clubsRes } = useClubs();

  const stats = statsRes?.data;
  const clubs = clubsRes?.data ?? [];

  const withReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setGender("Male");
    setNationality("");
    setClub("");
    setPage(1);
  };

  // Club is filtered client-side: the rankings endpoint takes a club id, but
  // filtering here keeps each player's true overall position visible.
  const allPlayers = data?.data ?? [];
  const players = club
    ? allPlayers.filter((p) => String(p.club?._id) === club)
    : allPlayers;

  const total = data?.totalResults ?? 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <>
      <Container className="pt-10 pb-8 md:pt-12">
        <div className="flex flex-col gap-8 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-navy md:text-4xl">
              World Rankings
            </h1>
            <p className="mt-3 max-w-lg text-base text-muted">
              Explore the world's top padel players and see how the rankings
              evolve.
            </p>
            {stats?.lastUpdated && (
              <p className="mt-3 text-sm text-muted">
                Last updated: {normalDate(stats.lastUpdated)}
              </p>
            )}
          </div>

          <div className="w-full xl:max-w-2xl">
            <StatStrip
              items={[
                {
                  icon: <UsersIcon />,
                  value: formatNumber(stats?.rankedPlayers ?? 0),
                  label: "Ranked Players",
                },
                {
                  icon: <GlobeIcon />,
                  value: formatNumber(stats?.countries ?? 0),
                  label: "Countries",
                },
                {
                  icon: <TrophyIcon className="text-gold" />,
                  value: formatNumber(stats?.tournaments ?? 0),
                  label: "Tournaments",
                },
                {
                  icon: <RefreshIcon />,
                  value: "Weekly",
                  label: "Updated",
                  sublabel: "Every Monday",
                },
              ]}
            />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {SEGMENTS.map((segment) => (
            <button
              key={segment.value}
              type="button"
              onClick={() => withReset(setGender)(segment.value)}
              className={`h-11 rounded-sm border px-5 text-sm font-semibold transition-colors ${
                gender === segment.value
                  ? "border-primary bg-primary text-white"
                  : "border-border bg-surface text-navy hover:bg-bg"
              }`}
            >
              {segment.label}
            </button>
          ))}
        </div>
      </Container>

      <Container className="pb-16 md:pb-20">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* -------------------------------- Filters ------------------------------- */}
          <aside className="w-full shrink-0 lg:w-72">
            <Card>
              <h2 className="mb-5 font-semibold text-navy">Filters</h2>

              <div className="mb-4">
                <Input
                  icon={<SearchIcon />}
                  placeholder="Search players…"
                  value={search}
                  onChange={(e) => withReset(setSearch)(e.target.value)}
                  aria-label="Search players"
                />
              </div>

              <div className="mb-4">
                <Label htmlFor="filter-country">Country</Label>
                <Select
                  id="filter-country"
                  value={nationality}
                  onChange={(e) => withReset(setNationality)(e.target.value)}
                >
                  <option value="">All Countries</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="mb-5">
                <Label htmlFor="filter-club">Club</Label>
                <Select
                  id="filter-club"
                  value={club}
                  onChange={(e) => withReset(setClub)(e.target.value)}
                >
                  <option value="">All Clubs</option>
                  {clubs.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>

              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-hover"
              >
                <RefreshIcon className="h-4 w-4" />
                Clear all filters
              </button>
            </Card>

            <div className="mt-5 overflow-hidden rounded-md bg-gradient-to-br from-primary to-[#1E3A8A] p-6 text-white">
              <TrendIcon className="h-6 w-6" />
              <h2 className="mt-4 text-lg font-bold">Track your progress</h2>
              <p className="mt-2 text-sm text-white/80">
                Create your profile and start climbing the rankings.
              </p>
              <Button to="/register" variant="inverse" size="sm" className="mt-5">
                Get Ranked Today
                <ArrowRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </aside>

          {/* -------------------------------- Table --------------------------------- */}
          <div className="min-w-0 flex-1">
            {isMixed ? (
              <Card>
                <EmptyState
                  icon="🎾"
                  message="Mixed and open rankings aren't published yet. Choose Men or Women to see the current standings."
                />
              </Card>
            ) : isFetching && !data ? (
              <Card>
                <Loader label="Loading rankings…" />
              </Card>
            ) : isError ? (
              <Card>
                <ErrorState message="We couldn't load the rankings." />
              </Card>
            ) : players.length === 0 ? (
              <Card>
                <EmptyState message="No players match your filters." />
              </Card>
            ) : (
              <Card padded={false} className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
                  <p className="text-sm text-muted">
                    Showing{" "}
                    <span className="font-semibold text-navy">
                      {rangeStart} – {rangeEnd}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-navy">
                      {formatNumber(total)}
                    </span>{" "}
                    players
                  </p>
                </div>

                {/* Desktop: a real table. Mobile: stacked cards (§18). */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[48rem]">
                    <thead>
                      <tr className="border-b border-border bg-bg text-left text-xs font-semibold uppercase tracking-wide text-muted">
                        <th className="w-20 px-5 py-3">Rank</th>
                        <th className="px-3 py-3">Player</th>
                        <th className="px-3 py-3">Country</th>
                        <th className="px-3 py-3">Club</th>
                        <th className="px-3 py-3 text-right">Points</th>
                        <th className="px-3 py-3 text-right">Change</th>
                        <th className="px-5 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {players.map((player) => (
                        <tr
                          key={player._id}
                          className="group transition-colors hover:bg-bg"
                        >
                          <td className="px-5 py-3">
                            <RankBadge rank={player.ranking} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-3">
                            <Link
                              to={`/players/${player.slug || player._id}`}
                              className="flex items-center gap-2.5"
                            >
                              <img
                                src={
                                  player.imgUrl ||
                                  `${process.env.PUBLIC_URL}/default-profile-image.jpg`
                                }
                                alt=""
                                className="h-9 w-9 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `${process.env.PUBLIC_URL}/default-profile-image.jpg`;
                                }}
                              />
                              <span className="font-semibold text-navy group-hover:text-primary">
                                {player.name}
                              </span>
                              {player.isClaimed && (
                                <VerifiedTick className="h-4 w-4" />
                              )}
                            </Link>
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-muted">
                            <CountryLabel code={player.nationality} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-muted">
                            {player.club?.name || "—"}
                          </td>
                          <td className="tabular px-3 py-3 text-right font-bold text-primary">
                            {formatNumber(player.rankingPoints)}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <RankMovement
                              rank={player.ranking}
                              previousRank={player.previousRank}
                            />
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Link
                              to={`/players/${player.slug || player._id}`}
                              aria-label={`View ${player.name}`}
                              className="text-muted transition-colors hover:text-primary"
                            >
                              <ChevronRightIcon className="h-5 w-5" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="divide-y divide-border md:hidden">
                  {players.map((player) => (
                    <li key={player._id}>
                      <Link
                        to={`/players/${player.slug || player._id}`}
                        className="flex items-center gap-3 px-4 py-3"
                      >
                        <RankBadge rank={player.ranking} size="sm" />
                        <img
                          src={
                            player.imgUrl ||
                            `${process.env.PUBLIC_URL}/default-profile-image.jpg`
                          }
                          alt=""
                          className="h-10 w-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${process.env.PUBLIC_URL}/default-profile-image.jpg`;
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-semibold text-navy">
                              {player.name}
                            </span>
                            {player.isClaimed && (
                              <VerifiedTick className="h-4 w-4 shrink-0" />
                            )}
                          </div>
                          <CountryLabel
                            code={player.nationality}
                            className="mt-0.5 text-muted"
                          />
                        </div>
                        <div className="text-right">
                          <div className="tabular font-bold text-primary">
                            {formatNumber(player.rankingPoints)}
                          </div>
                          <RankMovement
                            rank={player.ranking}
                            previousRank={player.previousRank}
                          />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-border px-5 py-4">
                  <Pagination
                    page={page}
                    totalPages={data?.totalPages ?? 1}
                    onChange={setPage}
                  />
                </div>
              </Card>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default SearchRankings;
